// Standard Library
// 3rd Party
// Local

/**
 * SVG Icon Element
 * Displays a single icon from an icon set
 * Uses shadow DOM for encapsulation
 * Icon reference format: iconset-name:icon-id
 */
class SvgIconElement extends HTMLElement {
  /**
   * Gets the list of attributes to observe for changes
   * @returns {string[]} Array of attribute names to observe
   */
  static get observedAttributes() {
    return ['icon'];
  }

  /**
   * Creates a new SVG icon element
   */
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this._DEFAULT_ICONSET = 'icons';

    // Add styles to shadow DOM
    const style = document.createElement('style');
    style.textContent = `
      :host {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 1.2em;
        height: 1.2em;
        vertical-align: middle;
      }
      svg {
        width: 100%;
        height: 100%;
        fill: currentColor;
      }
    `;
    this.shadowRoot.appendChild(style);
  }

  /**
   * Sets the icon to display
   * @param {string} newIcon - The icon identifier (format: iconset:icon-name)
   */
  set icon(newIcon) {
    this.setAttribute('icon', newIcon);
  }

  /**
   * Gets the current icon identifier
   * @returns {string} The current icon identifier
   */
  get icon() {
    return this._icon;
  }

  /**
   * Handles attribute changes
   * @param {string} name - The name of the changed attribute
   * @param {string} oldValue - The previous value of the attribute
   * @param {string} newValue - The new value of the attribute
   */
  attributeChangedCallback(name, oldValue, newValue) {
    this[`_${name}`] = newValue;
    if (name === 'icon') {
      this._iconChanged(newValue);
    }
  }

  /**
   * Handles icon changes
   * @param {string} icon - The new icon identifier
   */
  _iconChanged(icon) {
    const parts = (icon || '').split(':');
    this._iconName = parts.pop();
    this._iconsetName = parts.pop() || this._DEFAULT_ICONSET;
    this._updateIcon();
  }

  /**
   * Updates the displayed icon
   */
  _updateIcon() {
    if (this._iconName === '') {
      if (this._iconset) {
        this._iconset.removeIcon(this);
      }
    } else if (this._iconsetName) {
      this._iconset = document.querySelector(`svg-icon-set[name="${this._iconsetName}"]`);
      if (this._iconset) {
        this._iconset.applyIcon(this, this._iconName);
      }
    }
  }
}

export { SvgIconElement };
